import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { ResumeIndicatorB6ByHealthTeamData } from "@/services/esus/indicator/esb/b6/getResumeIndicatorB6ByHealthTeam";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getIndicatorClassification, IndicatorEsbEnum } from "@/utils/indicatorsEsb";
import { calculatePercentage } from "@/utils/calculatePercentage";

import { IndicatorEsbB6HealthTeamsFilters } from "./IndicatorEsbB6HealthTeamsFilters";
import { IndicatorEsbB6HealthTeamsClassificationTable } from "./IndicatorEsbB6HealthTeamsTable";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsbB6HealthTeamsProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isLoading: boolean;
	paginatedHealthTeamsClassifications: Paginated<ResumeIndicatorB6ByHealthTeamData>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllResumeIndicatorB6ByHealthTeam: () => Promise<ResumeIndicatorB6ByHealthTeamData[]>;
};

export function IndicatorEsbB6HealthTeams({
	selectedQuarter,
	search,
	setSearch,
	isLoading,
	paginatedHealthTeamsClassifications,
	pageNumber,
	setPageNumber,
	handleGetAllResumeIndicatorB6ByHealthTeam,
}: Readonly<IndicatorEsbB6HealthTeamsProps>) {
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const healthTeamsClassifications = await handleGetAllResumeIndicatorB6ByHealthTeam();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(healthTeamsClassifications).length > 0) {
			const excelData: GenericObject[] = transformIndicatorB6AttendancesToGenericObjectArray(
				healthTeamsClassifications
			);
			exportExcel(
				excelData,
				`Procedimentos por equipe - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorB6AttendancesToGenericObjectArray(
		healthTeamClassifications: ResumeIndicatorB6ByHealthTeamData[]
	) {
		return healthTeamClassifications.map(
			(healthTeamClassification) =>
				({
					"Equipe ESB": healthTeamClassification.esbHealthTeamName,
					"Equipe ESB INE": healthTeamClassification.esbHealthTeamIne,
					"Equipe ESF": healthTeamClassification.linkedHealthTeamName,
					"Equipe ESF INE": healthTeamClassification.linkedHealthTeamIne,
					"Procedimentos TRA em cidadãos vinculados":
						healthTeamClassification.totalAtraumaticRestorativeTreatmentProceduresLinked ??
						0,
					"Procedimentos TRA em cidadãos não vinculados":
						(healthTeamClassification.totalAtraumaticRestorativeTreatmentProcedures ??
							0) -
						healthTeamClassification.totalAtraumaticRestorativeTreatmentProceduresLinked,
					"Procedimentos TRA":
						healthTeamClassification.totalAtraumaticRestorativeTreatmentProcedures,
					"Procedimentos restauradores em cidadãos vinculados":
						healthTeamClassification.totalProceduresLinked ?? 0,
					"Procedimentos restauradores em cidadãos não vinculados":
						(healthTeamClassification.totalProcedures ?? 0) -
						healthTeamClassification.totalProceduresLinked,
					"Procedimentos restauradores": healthTeamClassification.totalProcedures,
					"% Classificação": `${calculatePercentage(
						healthTeamClassification.totalAtraumaticRestorativeTreatmentProcedures,
						healthTeamClassification.totalProcedures
					)}%`,
					Classificação: getIndicatorClassification(
						IndicatorEsbEnum.B6,
						calculatePercentage(
							healthTeamClassification.totalAtraumaticRestorativeTreatmentProcedures,
							healthTeamClassification.totalProcedures
						)
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsbB6HealthTeamsFilters
							search={search}
							setSearch={setSearch}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsbB6HealthTeamsClassificationTable
							healthTeamsClassifications={paginatedHealthTeamsClassifications.data}
							isLoading={isLoading}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedHealthTeamsClassifications?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedHealthTeamsClassifications.pageSize}
								totalPages={paginatedHealthTeamsClassifications.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
			</Col>
		</Row>
	);
}
